import axiosInstance from "../axiosInstance/axiosInstance";
import queryClient from "./queryClient";

// Long enough to cover a navigation (the hotel screen into a request form both
// want the same booking) without holding on to a status the hotel may have
// moved on from.
const BOOKING_STALE_TIME = 10 * 1000;

// The hotel's service catalogue changes far less often than a guest browses it.
const HOTEL_SERVICES_STALE_TIME = 5 * 60 * 1000;

// The booking fields every hotel and request-creation screen expects to be
// handed. Previously these were passed around in location.state, which meant
// none of those screens survived a refresh or could be linked to.
const toContext = (bookingId, booking) => {
    return {
        bookingId: bookingId,
        hotelId: booking?.hotel_id || "",
        hotelName: booking?.hotel_details?.name || "",
        hotelAddress: booking?.hotel_details?.address || "",
        checkInDate: booking?.checkin_date || "",
        checkOutDate: booking?.checkout_date || "",
        status: booking?.current_user_status || booking?.status || "",
        booking_code: booking?.booking_code || "",
        landingPhoto: booking?.hotel_details?.landing_photo || "",
    };
};

export const loadBookingContext = async (bookingId) => {
    const userId = localStorage.getItem("user_id");

    const data = await queryClient.fetchQuery({
        queryKey: ["booking", userId, bookingId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/api/v1/users/${userId}/bookings/${bookingId}`);
            // The single booking endpoint has been seen wrapped and unwrapped
            return response.data?.data || response.data;
        },
        staleTime: BOOKING_STALE_TIME,
        // Fail as fast as this call used to; the caller handles the error.
        retry: false,
    });

    const context = toContext(bookingId, data);

    if (context.status) {
        return context;
    }

    // current_user_status is per-guest, and older deployments of the single
    // booking endpoint do not carry it. Everything on the hotel screen is gated
    // on it, so fall back to the list, which always has it. Current backends
    // fill it in and never reach this.
    const listResponse = await axiosInstance.get(`/api/v1/users/${userId}/bookings`);
    const bookings = listResponse?.data?.data || [];
    const listed = bookings.find(booking => booking.id === bookingId);

    return listed ? toContext(bookingId, listed) : context;
};

// Every screen guards on the hotel actually offering the service before it
// lets a guest raise the request.
export const isServiceSupported = async (hotelId, serviceName) => {
    const services = await queryClient.fetchQuery({
        queryKey: ["hotel-services", hotelId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/api/v1/hotels/${hotelId}/services`);
            return response.data.data;
        },
        staleTime: HOTEL_SERVICES_STALE_TIME,
        retry: false,
    });

    if (!Array.isArray(services)) {
        console.log("Error: hotelServicesData is not an array", services);
        return true;
    }

    return services.some(service => service.name === serviceName);
};
